import { Dialog } from "@angular/cdk/dialog";
import {
    createNgModule,
    Inject,
    inject,
    Injectable,
    Injector,
    NgModuleRef,
    OnDestroy,
} from "@angular/core";

import { fromEvent, Subscription } from "rxjs";

import { Context, WINDOW } from "@hermes/app-core";
import type { VtoBeautyModalModule } from "@hermes/fragments/vto-beauty-modal";
import type { VtoModalModule } from "@hermes/fragments/vto-modal";
import { FeatureFlagFacade, VIRTUAL_TRY_ON } from "@hermes/states/flipper";
import { ModalFacade } from "@hermes/states/modal";
import { VtoFacade, VtoPageType } from "@hermes/states/vto";
import { VtoBeautyFacade } from "@hermes/states/vto-beauty";

@Injectable()
export class VtoModalService implements OnDestroy {
    private static moduleRef: NgModuleRef<
        VtoBeautyModalModule | VtoModalModule
    >;

    public facade!: VtoBeautyFacade | VtoFacade;

    private isJewelryVtoEnabled!: boolean;
    private subscription = new Subscription();

    constructor(
        private injector: Injector,
        public dialog: Dialog,
        private context: Context,
        private modalFacade: ModalFacade,
        private featureFlagFacade: FeatureFlagFacade,
        @Inject(WINDOW) private window: Window,
    ) {
        this.subscription.add(
            this.featureFlagFacade
                .isActivated(VIRTUAL_TRY_ON)
                .subscribe((isJewelryVtoEnabled) => {
                    this.isJewelryVtoEnabled = isJewelryVtoEnabled;
                    this.facade = isJewelryVtoEnabled
                        ? inject(VtoFacade)
                        : inject(VtoBeautyFacade);
                }),
        );

        if (this.context.isInBrowserMode()) {
            this.subscription.add(
                fromEvent(this.window, "pageshow").subscribe((event) => {
                    this.onPageShow(event as PageTransitionEvent);
                }),
            );
        }
    }

    // Pageshow event is sent to a Window when the browser
    // displays the window's document due to navigation
    public onPageShow(event: PageTransitionEvent): void {
        if (
            // Persisted indicates webpage is loading from a browser cache
            event.persisted
        ) {
            // Force to close or reset all modals
            this.modalFacade.closeModal(false);
            this.facade?.closeModal();
        }
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * openVtoModal
     * update page type, set vtoModal instance
     *
     @param pageType
     */
    public async openVtoModal(pageType: VtoPageType): Promise<void> {
        this.facade.updatePageType(pageType);
        this.facade.openModal(pageType);
        const configDialog = {
            panelClass: ["modal-service"],
            disableClose: true,
            ariaLabel: $localize`:@@hermes.product-page.vto-service:Virtual try-on service | Try different shades on your own face`,
        };

        if (this.isJewelryVtoEnabled) {
            this.dialog.open(await this.getVtoModalComponent(), configDialog);
        } else {
            this.dialog.open(
                await this.getVtoBeautyModalComponent(),
                configDialog,
            );
        }
    }

    public async getVtoBeautyModalComponent() {
        if (!VtoModalService.moduleRef) {
            const vtoBeautyModalModule = await import(
                "@hermes/fragments/vto-beauty-modal"
            );
            VtoModalService.moduleRef = createNgModule(
                vtoBeautyModalModule.VtoBeautyModalModule,
                this.injector,
            );
        }
        return (
            VtoModalService.moduleRef.instance as VtoBeautyModalModule
        ).resolveComponent();
    }

    public async getVtoModalComponent() {
        if (!VtoModalService.moduleRef) {
            const vtoModalModule = await import("@hermes/fragments/vto-modal");
            VtoModalService.moduleRef = createNgModule(
                vtoModalModule.VtoModalModule,
                this.injector,
            );
        }
        return (
            VtoModalService.moduleRef.instance as VtoModalModule
        ).resolveComponent();
    }
}
